/**
 * Which Safaricom this deployment is talking to.
 *
 * The worst outcome this system has is running sandbox in production and believing the payments
 * are real. The API logs it loudly at startup, but nobody reads a boot log after the first week —
 * so it is also on the screen, above the content, on every page, for as long as it is true.
 *
 * Deliberately not dismissible. A banner somebody can close is a banner that is closed.
 *
 * **One line, not a block.** It used to be a filled `Alert` with a title and a paragraph, which
 * took about 15% of the viewport on every page for a message that disappears entirely in
 * production — the dashboard's own chrome shouting louder than its data. The fix is subordination
 * without softening: the full sentence still reads, and the stub case still uses danger, but it
 * occupies a strip rather than a card. Unmissable and not dominant are compatible.
 *
 * It lives inside the shell rather than in a feature of its own. It is chrome — it wraps whatever
 * page is routed, it has no screen of its own, and a sibling feature importing it would be the
 * cross-feature dependency the boundary rules exist to prevent.
 */

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import { Permission, can, selectUser, useGetMpesaEnvironmentQuery } from '@pos/shared';

import { useAppSelector } from '@/app/hooks';

export function MpesaBanner() {
  const user = useAppSelector(selectUser);
  const mayView = can(user, Permission.PaymentView);

  const { data } = useGetMpesaEnvironmentQuery(undefined, { skip: !mayView });

  // Production and working is the case that needs no banner. Everything else does.
  if (!mayView || data === undefined) return null;
  if (data.enabled && data.isProduction && !data.isStubbed) return null;

  const { severity, title, body } = messageFor(data);

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: { xs: 2, md: 3 },
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: `var(--pos-${severity}-border)`,
        bgcolor: `var(--pos-${severity}-subtle)`,
        color: `var(--pos-${severity}-subtle-text)`,
      }}
    >
      <WarningAmberOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 0, sm: 0.75 }}
        sx={{ minWidth: 0, alignItems: { sm: 'baseline' } }}
      >
        <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {title}.
        </Typography>
        {/* The consequence never truncates on a phone — it wraps. Which sentence someone needs is
            not a thing this component gets to guess. */}
        <Typography component="span" sx={{ fontSize: 12.5, opacity: 0.92 }}>
          {body}
        </Typography>
      </Stack>
    </Box>
  );
}

function messageFor(data: {
  enabled: boolean;
  environment: string;
  isProduction: boolean;
  isStubbed: boolean;
}): { severity: 'danger' | 'warning' | 'info'; title: string; body: string } {
  if (!data.enabled) {
    return {
      severity: 'info',
      title: 'M-Pesa is switched off',
      body: 'Cash and bank transfers work normally. Nobody can send an M-Pesa request until credentials are configured.',
    };
  }

  if (data.isStubbed) {
    // The loudest of the three. Nothing reaches Safaricom at all, so every confirmation on every
    // screen behind this banner is invented.
    return {
      severity: 'danger',
      title: 'M-Pesa is running against a local stub',
      body: 'No prompt reaches any phone and no money moves. Every payment shown as confirmed here is test data.',
    };
  }

  return {
    severity: 'warning',
    title: 'M-Pesa is in the sandbox',
    body: 'These are real requests to Safaricom’s test system, not real money. Nothing on this screen has reached a bank account.',
  };
}
